/**
 * Standardized empty state pattern for client portal.
 * Pattern: What happened + next action + expected outcome.
 * Use for section-level and page-level empty surfaces.
 */
import React from 'react';

export interface EmptyStateProps {
  /** Short headline describing the empty state (e.g. "No documents yet") */
  headline: string;
  /** Expected outcome or next-step guidance */
  message?: string;
  /** Primary action (e.g. button or link) */
  action?: React.ReactNode;
  /** Optional icon element */
  icon?: React.ReactNode;
  /** Layout: card (full section) or inline (compact) */
  variant?: 'card' | 'inline';
  className?: string;
  /** Optional children for complex custom actions/content below standard action */
  children?: React.ReactNode;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  headline,
  message,
  action,
  icon,
  variant = 'card',
  className = '',
  children
}) => {
  const baseClasses = 'rounded-2xl border border-primary-200 bg-primary-50/30 p-6';
  const inlineClasses = 'rounded-xl border border-primary-200 bg-primary-50/20 py-6 px-4';

  if (variant === 'inline') {
    return (
      <div
        className={`text-center ${inlineClasses} ${className}`}
        role="status"
        aria-live="polite"
      >
        {icon && <div className="flex justify-center mb-2">{icon}</div>}
        <p className="font-semibold text-primary-800">{headline}</p>
        {message && <p className="text-sm text-primary-600 mt-1">{message}</p>}
        {action && <div className="mt-4 flex justify-center">{action}</div>}
        {children}
      </div>
    );
  }

  return (
    <div
      className={`text-center ${baseClasses} ${className}`}
      role="status"
      aria-live="polite"
    >
      {icon && <div className="flex justify-center mb-3">{icon}</div>}
      <h3 className="text-lg font-semibold text-primary-800">{headline}</h3>
      {message && <p className="text-primary-600 mt-2 max-w-md mx-auto">{message}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
      {children}
    </div>
  );
};

export default EmptyState;
